'use client'

import React, { useMemo } from 'react';
import Stack from '@mui/material/Stack';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import InfoCircleIcon from './vectors/InfoCircleIcon';
import SearchIcon from './vectors/SearchIcon';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import MainLayout from 'components/MainLayout';
import LinkBreakIcon from './vectors/LinkBreakIcon';

const errorData: Record<number | string, { title: string; description: string; Icon: React.ComponentType<SvgIconProps> }> = {
  404: {
    title: 'Page not found',
    description: 'Sorry, this page could not be found',
    Icon: SearchIcon,
  },
  500: {
    title: 'Internal server error',
    description: 'Please, try to refresh the page or come back later',
    Icon: InfoCircleIcon,
  },
  SIGN_IN_TOKEN_NOT_FOUND: {
    title: 'Login link has expired',
    description: 'Please try to log in once more.',
    Icon: LinkBreakIcon,
  }
};

interface Props {
  pushContent?: boolean;
  statusCode: number | string;
}

export default function ErrorPage(props: Props) {
  const { statusCode, pushContent = false } = props;

  const { title, description, Icon } = useMemo(() => errorData[statusCode] || errorData[500], [statusCode]);

  return (
    <MainLayout
      pushContent={pushContent}
      position="fixed"
      left="0"
      top="0"
      height="100%"
      width="100%"
      bgcolor="background.default"
      sx={{ zIndex: (theme) => theme.zIndex.appBar - 10 }}
    >
      <Stack
        width="100%"
        height="100%"
        direction="column"
        justifyContent="center"
        alignItems="center"
        px={{ xs: 2, md: '120px' }}
      >
        <Icon color="disabled" sx={{ fontSize: { xs: '48px', md: '60px' } }} />
        <Stack gap={2} direction="row" alignItems="center" mt={3} mb={2}>
          <Typography variant="h1">{statusCode ?? 500}</Typography>
          <Divider orientation="vertical" sx={{ height: { xs: '24px', md: '32px' } }} />
          <Typography variant="h1">{title}</Typography>
        </Stack>
        <Typography color="text.background.medium" textAlign="center">
          {description}
        </Typography>
      </Stack>
    </MainLayout>
  );
}
